// Injects SEO tags and JSON-LD structured data for a page. Generated automatically from the CMS.
const setMeta = (selector, attrs) => {
    let el = document.head.querySelector(selector);
    if (!el) {
        el = document.createElement(attrs.tag || "meta");
        document.head.appendChild(el);
    }
    Object.entries(attrs).forEach(([k, v]) => { if (k !== "tag") el.setAttribute(k, v); });
    el.setAttribute("data-rhm-seo", "1");
    return el;
};

// Anything that is not the real site (the preview environment) must stay out of the index,
// otherwise it competes with rehealthme.com for the brand's own name.
let offSite = false;
export const markOffSite = (value) => { offSite = value; };

export function applySeo({ title, description, canonical, image, noindex, jsonLd = [] }) {
    if (title) document.title = title;
    noindex = noindex || offSite;
    if (description !== undefined) setMeta('meta[name="description"]', { name: "description", content: description || "" });
    setMeta('meta[name="robots"]', { name: "robots", content: noindex ? "noindex, nofollow" : "index, follow" });
    if (canonical) setMeta('link[rel="canonical"]', { tag: "link", rel: "canonical", href: canonical });
    setMeta('meta[property="og:title"]', { property: "og:title", content: title || "" });
    setMeta('meta[property="og:description"]', { property: "og:description", content: description || "" });
    setMeta('meta[property="og:type"]', { property: "og:type", content: "article" });
    if (canonical) setMeta('meta[property="og:url"]', { property: "og:url", content: canonical });
    if (image) setMeta('meta[property="og:image"]', { property: "og:image", content: image });
    setMeta('meta[name="twitter:card"]', { name: "twitter:card", content: image ? "summary_large_image" : "summary" });
    setMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title || "" });
    if (image) setMeta('meta[name="twitter:image"]', { name: "twitter:image", content: image });

    document.head.querySelectorAll('script[data-rhm-jsonld="1"]').forEach((s) => s.remove());
    jsonLd.filter(Boolean).forEach((data) => {
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.dataset.rhmJsonld = "1";
        s.textContent = JSON.stringify(data);
        document.head.appendChild(s);
    });
}

export function articleJsonLd(post, url, conf = {}) {
    const author = post.author || {};
    const reviewer = conf.founder || {};
    return {
        "@context": "https://schema.org", "@type": "BlogPosting",
        headline: post.seo?.seo_title || post.title,
        description: post.seo?.meta_description || post.excerpt || "",
        image: post.featured_image?.url ? [absolute(post.featured_image.url)] : undefined,
        datePublished: post.published_at || undefined,
        dateModified: post.updated_at || post.published_at || undefined,
        articleSection: post.category?.name,
        keywords: (post.tags || []).join(", ") || undefined,
        wordCount: post.word_count || undefined,
        mainEntityOfPage: { "@type": "WebPage", "@id": url },
        author: { "@type": author.is_brand ? "Organization" : "Person", name: author.name || "ReHealthMe" },
        reviewedBy: reviewer.name ? {
            "@type": "Person", name: reviewer.name,
            jobTitle: reviewer.job_title || undefined,
            hasCredential: reviewer.credentials || undefined,
        } : undefined,
        publisher: { "@type": "Organization", name: conf.brand_name || "ReHealthMe", url: window.location.origin },
    };
}

export function breadcrumbJsonLd(post, url) {
    const items = [
        { name: "Home", item: window.location.origin },
        { name: "Blog", item: `${window.location.origin}/blog` },
    ];
    if (post.category) items.push({ name: post.category.name, item: `${window.location.origin}/blog/category/${post.category.slug}` });
    items.push({ name: post.title, item: url });
    return {
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        itemListElement: items.map((it, i) => ({ "@type": "ListItem", position: i + 1, name: it.name, item: it.item })),
    };
}

export function faqJsonLd(post) {
    const faqs = (post.blocks || []).filter((b) => b.type === "faq").flatMap((b) => b.items || [])
        .filter((i) => i.question && i.answer);
    if (!faqs.length) return null;
    return {
        "@context": "https://schema.org", "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
            "@type": "Question", name: f.question,
            acceptedAnswer: { "@type": "Answer", text: f.answer.replace(/<[^>]+>/g, " ").trim() },
        })),
    };
}

export const absolute = (url = "") => (url.startsWith("http") ? url : `${window.location.origin}${url}`);

// ---------------------------------------------------------------- entity + commerce schema
export function organizationJsonLd(conf = {}) {
    const origin = window.location.origin;
    const founder = conf.founder || {};
    const clinic = conf.clinic || {};
    return {
        "@context": "https://schema.org", "@type": "Organization",
        "@id": `${origin}/#organization`,
        name: conf.brand_name || "ReHealthMe",
        legalName: conf.legal_name || conf.brand_name || "ReHealthMe",
        url: origin,
        slogan: conf.tagline || undefined,
        description: conf.description || undefined,
        logo: conf.logo_url ? absolute(conf.logo_url) : undefined,
        image: conf.social_image ? absolute(conf.social_image) : undefined,
        sameAs: [...Object.values(conf.profiles || {}), ...(conf.same_as || [])].filter(Boolean),
        founder: founder.name ? {
            "@type": "Person", name: founder.name,
            jobTitle: founder.job_title || undefined,
            honorificPrefix: founder.name.startsWith("Dr") ? "Dr." : undefined,
            hasCredential: founder.credentials || undefined,
            description: founder.bio || undefined,
            url: founder.profile_url ? absolute(founder.profile_url) : undefined,
        } : undefined,
        contactPoint: clinic.phone ? {
            "@type": "ContactPoint", telephone: clinic.phone, contactType: "customer support",
            areaServed: clinic.country || "IN", availableLanguage: ["en", "hi", "ur"],
        } : undefined,
    };
}

export function websiteJsonLd(conf = {}) {
    const origin = window.location.origin;
    return {
        "@context": "https://schema.org", "@type": "WebSite",
        "@id": `${origin}/#website`,
        url: origin,
        name: conf.brand_name || "ReHealthMe",
        description: conf.description || undefined,
        publisher: { "@id": `${origin}/#organization` },
        inLanguage: "en-IN",
    };
}

/** The clinic where the founder practises — helps Google connect the brand to a real place. */
export function clinicJsonLd(conf = {}) {
    const c = conf.clinic || {};
    if (!c.name || !c.city) return null;
    return {
        "@context": "https://schema.org", "@type": "MedicalClinic",
        name: c.name,
        url: c.google_profile_url || undefined,
        telephone: c.phone || undefined,
        medicalSpecialty: "PublicHealth",
        address: {
            "@type": "PostalAddress", streetAddress: c.street || undefined,
            addressLocality: c.city, addressRegion: c.region || undefined,
            postalCode: c.postal_code || undefined, addressCountry: c.country || "IN",
        },
    };
}

export function productJsonLd(product, conf = {}, reviews = null) {
    const origin = window.location.origin;
    const url = `${origin}/product/${product.slug}`;
    const inStock = product.purchasable !== false && !product.coming_soon;
    return {
        "@context": "https://schema.org", "@type": "Product",
        name: product.name,
        description: product.tagline || product.description || "",
        image: (product.images || []).map(absolute).slice(0, 5),
        sku: product.sku || product.product_code || undefined,
        mpn: product.product_code || undefined,
        category: product.category || undefined,
        url,
        brand: { "@type": "Brand", name: conf.brand_name || "ReHealthMe" },
        aggregateRating: reviews?.show_stars && reviews.count > 0 ? {
            "@type": "AggregateRating", ratingValue: String(reviews.average),
            reviewCount: String(reviews.count), bestRating: "5", worstRating: "1",
        } : undefined,
        review: (reviews?.reviews || []).length ? reviews.reviews.slice(0, 5).map((r) => ({
            "@type": "Review",
            reviewRating: { "@type": "Rating", ratingValue: String(r.rating), bestRating: "5" },
            author: { "@type": "Person", name: r.author_name },
            datePublished: (r.created_at || "").slice(0, 10) || undefined,
            name: r.title || undefined,
            reviewBody: r.body,
        })) : undefined,
        offers: product.price > 0 ? {
            "@type": "Offer", url, priceCurrency: "INR", price: String(product.price),
            availability: inStock ? "https://schema.org/InStock" : "https://schema.org/PreOrder",
            seller: { "@id": `${origin}/#organization` },
        } : undefined,
    };
}

export function crumbsJsonLd(trail = []) {
    const origin = window.location.origin;
    return {
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        itemListElement: trail.map((t, i) => ({
            "@type": "ListItem", position: i + 1, name: t.name,
            item: t.path ? `${origin}${t.path}` : undefined,
        })),
    };
}
